/**
 * Obsidian Tasks query blocks (```tasks) as live task lists. Supports the common
 * filters (done, due/scheduled dates, description, path, tags, priority), sorting
 * and limit; other lines are reported back so the block can say so.
 */
import { addDays, isOpen, type IsoDate, type TaskPriority } from '@nexus/core';
import type { Task } from './tasks';

export interface TaskQuery {
  /** `path` is the page's location, e.g. "Projecten/Licom Wallboard". */
  matches: (task: Task, path: string) => boolean;
  compare: (a: Task, b: Task) => number;
  limit: number | null;
  /** Lines this version does not understand (ignored). */
  unsupported: string[];
}

type Filter = (task: Task, path: string) => boolean;

const PRIORITY_RANK: Record<TaskPriority, number> = {
  highest: 0,
  high: 1,
  medium: 2,
  low: 3,
  lowest: 4,
};
// No priority sits between medium and low, as in Obsidian Tasks.
const priorityRank = (t: Task) => (t.priority ? PRIORITY_RANK[t.priority] : 2.5);

/** "05 Archief/Templates" → "archief/templates": numbering prefixes do not count. */
const normalPath = (s: string) =>
  s
    .split('/')
    .map((p) => p.trim().replace(/^\d+[\s._-]+/, ''))
    .join('/')
    .toLowerCase();

function dateWord(word: string, today: IsoDate): IsoDate | null {
  const w = word.trim().toLowerCase();
  if (w === 'today' || w === 'vandaag') return today;
  if (w === 'tomorrow' || w === 'morgen') return addDays(today, 1);
  if (w === 'yesterday' || w === 'gisteren') return addDays(today, -1);
  if (/^\d{4}-\d{2}-\d{2}$/.test(w)) return w;
  return null;
}

const DATE_FIELD: Record<string, (t: Task) => string | null> = {
  due: (t) => t.due,
  scheduled: (t) => t.scheduled,
  starts: (t) => t.start,
  start: (t) => t.start,
  done: (t) => t.doneAt,
};

function parseLine(line: string, today: IsoDate): Filter | null {
  const l = line.trim();
  const lower = l.toLowerCase();
  if (lower === 'not done') return (t) => isOpen(t.status);
  if (lower === 'done') return (t) => !isOpen(t.status);

  let m = /^(due|scheduled|starts?|done) (before|after|on) (.+)$/i.exec(l);
  if (m) {
    const field = DATE_FIELD[m[1]!.toLowerCase()]!;
    const date = dateWord(m[3]!, today);
    if (!date) return null;
    const op = m[2]!.toLowerCase();
    return (t) => {
      const v = field(t);
      if (!v) return false;
      return op === 'before' ? v < date : op === 'after' ? v > date : v === date;
    };
  }
  m = /^(no|has) (due|scheduled|start|done) date$/i.exec(l);
  if (m) {
    const field = DATE_FIELD[m[2]!.toLowerCase()]!;
    const want = m[1]!.toLowerCase() === 'has';
    return (t) => !!field(t) === want;
  }
  m = /^(description|path|heading) (includes|does not include) (.+)$/i.exec(l);
  if (m) {
    const what = m[1]!.toLowerCase();
    const yes = m[2]!.toLowerCase() === 'includes';
    const needle = what === 'path' ? normalPath(m[3]!) : m[3]!.trim().toLowerCase();
    if (what === 'heading') return null;
    return (t, path) => {
      const hay = what === 'path' ? normalPath(path) : t.title.toLowerCase();
      return hay.includes(needle) === yes;
    };
  }
  m = /^(tags?) (include|includes|do not include|does not include) #?(.+)$/i.exec(l);
  if (m) {
    const yes = !/not/i.test(m[2]!);
    const tag = m[3]!.trim().toLowerCase();
    return (t) => t.tags.some((x: string) => x.toLowerCase().includes(tag)) === yes;
  }
  m = /^priority is (not )?(highest|high|medium|low|lowest|none)$/i.exec(l);
  if (m) {
    const not = !!m[1];
    const p = m[2]!.toLowerCase();
    return (t) => ((p === 'none' ? !t.priority : t.priority === p) ? !not : not);
  }
  return null;
}

const SORT: Record<string, (a: Task, b: Task) => number> = {
  due: (a, b) => (a.due ?? '9999').localeCompare(b.due ?? '9999'),
  scheduled: (a, b) => (a.scheduled ?? '9999').localeCompare(b.scheduled ?? '9999'),
  priority: (a, b) => priorityRank(a) - priorityRank(b),
  description: (a, b) => a.title.localeCompare(b.title),
  status: (a, b) => Number(!isOpen(a.status)) - Number(!isOpen(b.status)),
};

export function parseTaskQuery(text: string, today: IsoDate): TaskQuery {
  const filters: Filter[] = [];
  const sorts: ((a: Task, b: Task) => number)[] = [];
  const unsupported: string[] = [];
  let limit: number | null = null;

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    let m = /^limit (?:to )?(\d+)(?: tasks?)?$/i.exec(line);
    if (m) {
      limit = Number(m[1]);
      continue;
    }
    m = /^sort by (\w+)( reverse)?$/i.exec(line);
    if (m && SORT[m[1]!.toLowerCase()]) {
      const by = SORT[m[1]!.toLowerCase()]!;
      sorts.push(m[2] ? (a, b) => -by(a, b) : by);
      continue;
    }
    if (/^(hide|show|short mode|explain|group by)\b/i.test(line)) continue; // layout only
    const f = parseLine(line, today);
    if (f) filters.push(f);
    else unsupported.push(line);
  }
  // Without a sort: open first, then by due date and priority (like the task views).
  if (!sorts.length) sorts.push(SORT.status!, SORT.due!, SORT.priority!);
  return {
    matches: (t, path) => filters.every((f) => f(t, path)),
    compare: (a, b) => {
      for (const s of sorts) {
        const d = s(a, b);
        if (d) return d;
      }
      return 0;
    },
    limit,
    unsupported,
  };
}

export function runTaskQuery(
  query: TaskQuery,
  tasks: Task[],
  pathOf: (task: Task) => string,
): Task[] {
  const hits = tasks.filter((t) => query.matches(t, pathOf(t))).sort(query.compare);
  return query.limit === null ? hits : hits.slice(0, query.limit);
}
